/**
 * Off-peak schedules for display: when an offer's off-peak prices apply
 * and when that next changes. A copy of packages/policy/src/off-peak.ts,
 * which the gateway bills by; off-peak.spec.ts fails if the two disagree.
 */

/** A busy window on some days, in the schedule's time zone. */
export interface PeakWindow {
	/** Days of the week, 0 = Sunday … 6 = Saturday. */
	days: number[];
	/** "HH:MM", inclusive. */
	start: string;
	/** "HH:MM", exclusive; "24:00" for midnight. */
	end: string;
}

/** USD per token, as decimal strings like the offer's price columns. */
export interface OffPeakPrices {
	input: string;
	output: string;
	cachedInput?: string | null;
}

export interface OffPeakSchedule {
	/** IANA time zone the windows are written in, e.g. "Asia/Shanghai" or "UTC". */
	timeZone: string;
	/** Busy windows; any other time is off-peak. */
	peak: PeakWindow[];
	/** Whole days that are off-peak however busy they'd be (public holidays), "YYYY-MM-DD" in the time zone. */
	offPeakDates?: string[];
	/** What customers pay off-peak. */
	prices: OffPeakPrices;
	/** What we pay off-peak, when known. */
	cost?: OffPeakPrices | null;
	/** Where the schedule came from (a provider's page, a supplier's sheet). */
	source?: string | null;
}

const WEEKDAYS: Record<string, number> = {
	Sun: 0,
	Mon: 1,
	Tue: 2,
	Wed: 3,
	Thu: 4,
	Fri: 5,
	Sat: 6,
};
const minutes = (hhmm: string) => {
	const [h = "0", m = "0"] = hhmm.split(":");
	const hourMinutes = Number(h) * 60;
	return hourMinutes + Number(m);
};

/** The local weekday, date and minute of the day at `at` in a time zone. */
export function localTime(timeZone: string, at: Date) {
	const parts = Object.fromEntries(
		new Intl.DateTimeFormat("en-US", {
			timeZone,
			weekday: "short",
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
			hour: "2-digit",
			minute: "2-digit",
			hourCycle: "h23",
		})
			.formatToParts(at)
			.map((p) => [p.type, p.value]),
	);
	const hourMinutes = Number(parts.hour) * 60;
	return {
		day: WEEKDAYS[parts.weekday ?? "Sun"] ?? 0,
		date: `${parts.year}-${parts.month}-${parts.day}`,
		minute: hourMinutes + Number(parts.minute),
	};
}

/** Whether a window covers this local day and minute (windows may run past midnight). */
function inWindow(window: PeakWindow, day: number, minute: number) {
	const start = minutes(window.start);
	const end = minutes(window.end);
	if (end > start) {
		return window.days.includes(day) && minute >= start && minute < end;
	}
	// Past midnight: the evening part belongs to the listed day, the morning part to the next.
	return (
		(window.days.includes(day) && minute >= start) ||
		(window.days.includes((day + 6) % 7) && minute < end)
	);
}

/** Whether `at` is off-peak under the schedule. Unknown or broken schedules count as peak. */
export function isOffPeak(
	schedule: OffPeakSchedule | null | undefined,
	at: Date,
): boolean {
	if (!schedule || !Array.isArray(schedule.peak)) {
		return false;
	}
	try {
		const local = localTime(schedule.timeZone, at);
		if (schedule.offPeakDates?.includes(local.date)) {
			return true;
		}
		return !schedule.peak.some((w) => inWindow(w, local.day, local.minute));
	} catch {
		return false;
	}
}

/**
 * When off-peak next starts or ends after `at`, for "off-peak now, ends in
 * 2 h". Checked minute by minute over the next 8 days; null if it never
 * changes in that time.
 */
export function nextChange(schedule: OffPeakSchedule, at: Date): Date | null {
	const now = isOffPeak(schedule, at);
	const start = new Date(Math.ceil(at.getTime() / 60_000) * 60_000);
	for (let i = 0; i <= 8 * 24 * 60; i += 1) {
		const offset = i * 60_000;
		const when = new Date(start.getTime() + offset);
		if (isOffPeak(schedule, when) !== now) {
			return when;
		}
	}
	return null;
}
